import { format } from 'date-fns';

export function getRandomAlphaNumeric(length = 6) {
  let text = '';
  while (length-- > 0) {
    const num = Math.round(Math.random() * 35);
    if (num > 9) {
      text += String.fromCharCode(num - 9 + 65);
    } else {
      text += String.fromCharCode(num + 48);
    }
  }
  return text;
}

/**
 * Formats a Date object to YYYY-MM-DD in local time
 */
export function formatDateOnly(date: Date | string | null | undefined): string {
  if (!date) return '';
  let targetDate: Date | null = null;
  if (date instanceof Date) {
    targetDate = isNaN(date.getTime()) ? null : date;
  } else if (typeof date === 'string') {
    targetDate = parseDateOnly(date);
  }
  if (!targetDate || isNaN(targetDate.getTime())) return '';
  const y = targetDate.getFullYear();
  const m = String(targetDate.getMonth() + 1).padStart(2, '0');
  const d = String(targetDate.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parses a YYYY-MM-DD (or ISO datetime) string as a local Date object.
 * Avoids `new Date("YYYY-MM-DD")` UTC-midnight day-shift in western timezones.
 */
export function parseDateOnly(dateString: string | Date | number | null | undefined | any): Date | null {
  if (!dateString) return null;
  if (dateString instanceof Date) {
    return isNaN(dateString.getTime()) ? null : dateString;
  }
  if (typeof (dateString as any)?.toDate === 'function') {
    const d = (dateString as any).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  if (typeof dateString === 'number') {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof dateString !== 'string') {
    const str = String(dateString);
    if (!str || str === '[object Object]') return null;
    dateString = str;
  }
  const datePart = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const parts = datePart.split('-');
  if (parts.length !== 3) {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? null : d;
  }
  const [y, m, d] = parts.map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

export function collide(el1, el2) {
  var rect1 = el1.getBoundingClientRect();
  var rect2 = el2.getBoundingClientRect();

  return !(
    rect1.top > rect2.bottom ||
    rect1.right < rect2.left ||
    rect1.bottom < rect2.top ||
    rect1.left > rect2.right
  );
}

const LOCALE = 'en-US';

export const formatter = {
  number: new Intl.NumberFormat(LOCALE, {
    maximumFractionDigits: 2,
  }),
  currency: new Intl.NumberFormat(LOCALE, {
    currency: 'USD',
    style: 'currency',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }),
  currencyFlexible: new Intl.NumberFormat(LOCALE, {
    currency: 'USD',
    style: 'currency',
    minimumFractionDigits: 0,
  }),
  percent: new Intl.NumberFormat(LOCALE, {
    maximumFractionDigits: 2,
    style: 'percent',
  }),
  weekdayShort: new Intl.DateTimeFormat(LOCALE, {
    weekday: 'narrow',
  }),
  capitalize: {
    format(text) {
      if (text) {
        text = text.toString();
        return text[0].toUpperCase() + text.substring(1);
      }
      return text;
    },
  },
  date: {
    format(date) {
      const specificDate = typeof date === 'string' ? (parseDateOnly(date) ?? new Date()) : date;
      return format(specificDate, 'MMM d, yyyy');
    },
  },
  romi: new Intl.NumberFormat(LOCALE, {
    maximumFractionDigits: 1,
  }),
};

export function getFetcher(axiosFunction, ...rest) {
  return (url) =>
    axiosFunction
      ? axiosFunction(url, ...rest).then((res) => res.data)
      : Promise.reject('Fetcher unavailable');
}

export const MONTHS = [
  'January',
  'February',
  'March',
  'April',
  'May',
  'June',
  'July',
  'August',
  'September',
  'October',
  'November',
  'December',
];

export const regex = {
  email:
    /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  phone: /^\(?\d{3}\)?-? *\d{3}-? *-?\d{4}$/,
  zip5: /^\d{5}$/,
  looseZip5: /^\d{4,5}$/,
  mobileUA:
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino|android|ipad|playbook|silk/i,
  tabletUA:
    /1207|6310|6590|3gso|4thp|50[1-6]i|770s|802s|a wa|abac|ac(er|oo|s\-)|ai(ko|rn)|al(av|ca|co)|amoi|an(ex|ny|yw)|aptu|ar(ch|go)|as(te|us)|attw|au(di|\-m|r |s )|avan|be(ck|ll|nq)|bi(lb|rd)|bl(ac|az)|br(e|v)w|bumb|bw\-(n|u)|c55\/|capi|ccwa|cdm\-|cell|chtm|cldc|cmd\-|co(mp|nd)|craw|da(it|ll|ng)|dbte|dc\-s|devi|dica|dmob|do(c|p)o|ds(12|\-d)|el(49|ai)|em(l2|ul)|er(ic|k0)|esl8|ez([4-7]0|os|wa|ze)|fetc|fly(\-|_)|g1 u|g560|gene|gf\-5|g\-mo|go(\.w|od)|gr(ad|un)|haie|hcit|hd\-(m|p|t)|hei\-|hi(pt|ta)|hp( i|ip)|hs\-c|ht(c(\-| |_|a|g|p|s|t)|tp)|hu(aw|tc)|i\-(20|go|ma)|i230|iac( |\-|\/)|ibro|idea|ig01|ikom|im1k|inno|ipaq|iris|ja(t|v)a|jbro|jemu|jigs|kddi|keji|kgt( |\/)|klon|kpt |kwc\-|kyo(c|k)|le(no|xi)|lg( g|\/(k|l|u)|50|54|\-[a-w])|libw|lynx|m1\-w|m3ga|m50\/|ma(te|ui|xo)|mc(01|21|ca)|m\-cr|me(rc|ri)|mi(o8|oa|ts)|mmef|mo(01|02|bi|de|do|t(\-| |o|v)|zz)|mt(50|p1|v )|mwbp|mywa|n10[0-2]|n20[2-3]|n30(0|2)|n50(0|2|5)|n7(0(0|1)|10)|ne((c|m)\-|on|tf|wf|wg|wt)|nok(6|i)|nzph|o2im|op(ti|wv)|oran|owg1|p800|pan(a|d|t)|pdxg|pg(13|\-([1-8]|c))|phil|pire|pl(ay|uc)|pn\-2|po(ck|rt|se)|prox|psio|pt\-g|qa\-a|qc(07|12|21|32|60|\-[2-7]|i\-)|qtek|r380|r600|raks|rim9|ro(ve|zo)|s55\/|sa(ge|ma|mm|ms|ny|va)|sc(01|h\-|oo|p\-)|sdk\/|se(c(\-|0|1)|47|mc|nd|ri)|sgh\-|shar|sie(\-|m)|sk\-0|sl(45|id)|sm(al|ar|b3|it|t5)|so(ft|ny)|sp(01|h\-|v\-|v )|sy(01|mb)|t2(18|50)|t6(00|10|18)|ta(gt|lk)|tcl\-|tdg\-|tel(i|m)|tim\-|t\-mo|to(pl|sh)|ts(70|m\-|m3|m5)|tx\-9|up(\.b|g1|si)|utst|v400|v750|veri|vi(rg|te)|vk(40|5[0-3]|\-v)|vm40|voda|vulc|vx(52|53|60|61|70|80|81|83|85|98)|w3c(\-| )|webc|whit|wi(g |nc|nw)|wmlb|wonu|x700|yas\-|your|zeto|zte\-/i,
  tridentUA: /Trident.*rv\:11\./,
  commaSeparated: /\s*[,]\s*/,
  // url: /^(https?([:][/]{2}))?[-a-zA-Z0-9@:%._\+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_\+.~#?&//=]*)$/,
  url: new RegExp(
    '^(https?:\\/\\/)?' + // protocol
    '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    '(\\#[-a-z\\d_]*)?$',
    'i'
  ), // fragment locator
  crid: /^[1-9]\d{0,14}$/,
  mid: /^[1-9](\d{5}|\d{8})$/,
  serial: /^(\d{6}|\d{9})$/,
  integer: /^\d+$/,
  range: /^[([]-?\d+(.\d+)?,\s*-?\d+(.\d+)?[)\]]$/,
  nonNumeric: /[^0123456789\-,.+]/g,
};

export const regexColumns = {
  firstName: /^(person[\s_-]?)?first[\s_-]?name$/i,
  lastName: /^(person[\s_-]?)?last[\s_-]?name$/i,
  company: /^company([\s_-]?name)?$/i,
  address:
    /^((physical|street)[\s_-]?)?(address|location)([\s_-]?line1)?|house[\s_-]?number[\s_-]?and[\s_-]?street$/i,
  city: /^(physical[\s_-]?)?city$/i,
  state: /^(physical[\s_-]?)?(state|st)$/i,
  postal:
    /^(physical[\s_-]?)?(zip([\s_-]?code)?|(postal|post)([\s_-]?code)?)$/i,
  date: /^((conversion|order|churn|invoice)[\s_-]?)?date([\s_-]?time)?$/i,
  value: /^((conversion|order|churn|invoice)[\s_-]?)?value$/i,
  age: /^age$/i,
  income: /^income$/i,
  gender: /^gender$/i,
  barcode: /^(usps_?|imb?_?)?barcode$/i,
  barcodeMID: /^(usps_?|imb?_?)?(barcode_?)?(mid|mailer_?id)$/i,
  storeName: /^(store[\s_-]?)?name$/i,
  category: /^category$/i,
};

export const us_administrative_areas_list = [
  { long: 'Alabama', short: 'AL' },
  { long: 'Alaska', short: 'AK' },
  { long: 'Arizona', short: 'AZ' },
  { long: 'Arkansas', short: 'AR' },
  { long: 'California', short: 'CA' },
  { long: 'Colorado', short: 'CO' },
  { long: 'Connecticut', short: 'CT' },
  { long: 'Delaware', short: 'DE' },
  { long: 'District of Columbia', short: 'DC' },
  { long: 'Florida', short: 'FL' },
  { long: 'Georgia', short: 'GA' },
  { long: 'Hawaii', short: 'HI' },
  { long: 'Idaho', short: 'ID' },
  { long: 'Illinois', short: 'IL' },
  { long: 'Indiana', short: 'IN' },
  { long: 'Iowa', short: 'IA' },
  { long: 'Kansas', short: 'KS' },
  { long: 'Kentucky', short: 'KY' },
  { long: 'Louisiana', short: 'LA' },
  { long: 'Maine', short: 'ME' },
  { long: 'Maryland', short: 'MD' },
  { long: 'Massachusetts', short: 'MA' },
  { long: 'Michigan', short: 'MI' },
  { long: 'Minnesota', short: 'MN' },
  { long: 'Mississippi', short: 'MS' },
  { long: 'Missouri', short: 'MO' },
  { long: 'Montana', short: 'MT' },
  { long: 'Nebraska', short: 'NE' },
  { long: 'Nevada', short: 'NV' },
  { long: 'New Hampshire', short: 'NH' },
  { long: 'New Jersey', short: 'NJ' },
  { long: 'New Mexico', short: 'NM' },
  { long: 'New York', short: 'NY' },
  { long: 'North Carolina', short: 'NC' },
  { long: 'North Dakota', short: 'ND' },
  { long: 'Ohio', short: 'OH' },
  { long: 'Oklahoma', short: 'OK' },
  { long: 'Oregon', short: 'OR' },
  { long: 'Pennsylvania', short: 'PA' },
  { long: 'Rhode Island', short: 'RI' },
  { long: 'South Carolina', short: 'SC' },
  { long: 'South Dakota', short: 'SD' },
  { long: 'Tennessee', short: 'TN' },
  { long: 'Texas', short: 'TX' },
  { long: 'Utah', short: 'UT' },
  { long: 'Vermont', short: 'VT' },
  { long: 'Virginia', short: 'VA' },
  { long: 'Washington', short: 'WA' },
  { long: 'West Virginia', short: 'WV' },
  { long: 'Wisconsin', short: 'WI' },
  { long: 'Wyoming', short: 'WY' },
  { long: 'American Samoa', short: 'AS' },
  { long: 'Guam', short: 'GU' },
  { long: 'Northern Mariana Islands', short: 'MP' },
  { long: 'Puerto Rico', short: 'PR' },
  { long: 'U.S. Virgin Islands', short: 'VI' },
  { long: 'U.S. Minor Outlying Islands', short: 'UM' },
];

export const ALL_PREDICTION_MODEL_TYPES = [
  {
    text: 'Lookalike',
    value: 'lookalike',
    visible: false,
  },
  {
    text: 'Lead scoring',
    value: 'lead_scoring',
    visible: true,
  },
  {
    text: 'Churn reduction',
    value: 'churn_reduction',
    visible: true,
  },
  {
    text: 'Persona assignment',
    value: 'persona_assignment',
    visible: true,
  },
  {
    text: 'Persona assignment',
    value: 'persona',
    visible: true,
  },
  {
    text: 'Data enrichment',
    value: 'data_enrichment',
    visible: true,
  },
  {
    text: 'ZIP™ Code scores',
    value: 'ad_cost_optimizer',
    visible: true,
    demo: true,
  },
  {
    text: 'Other',
    value: 'other',
    visible: true,
    other: true,
  },
];

export function getAudienceType(type, modelType) {
  switch (type) {
    // case 'uploaded':
    //   return 'Uploaded';
    // case 'userSearch':
    //   return 'Purchased';
    // case 'lookalike':
    //   return 'Lookalike';
    case 8:
      return 'Purchased';
    case 9: {
      const subType = (
        ALL_PREDICTION_MODEL_TYPES.find((t) => t.value == modelType) ||
        ALL_PREDICTION_MODEL_TYPES[0]
      ).text;
      if (modelType) {
        return subType;
      }
      return `Prediction (${subType})`;
    }
  }
  return 'Uploaded';
}

export function prependZeroes(text, count) {
  for (let i = 0; i < count; ++i) text = '0' + text;
  return text;
}

export function padString(str, length, padding = '0', padEnd = false) {
  if (!str) {
    str = '';
  } else if (typeof str != 'string') {
    str = str.toString();
  }
  const required = length - str.length;
  for (let j = 0; j < required; ++j) {
    if (padEnd) {
      str += padding;
    } else {
      str = padding + str;
    }
  }
  return str;
}

export function fixedDigits(number, digitsCount) {
  number = (number % Math.pow(10, digitsCount)).toString();
  if (number.length < 2) {
    number = prependZeroes(number, 2 - number.length);
  }
  return number;
}

export function getBadgeClass(status) {
  switch (status) {
    case 'pending':
      return 'badge-warning pt-07';
    case 'active':
      return 'badge-primary pt-07';
    case 'ready':
      return 'badge-success pt-07';
    case 'paused':
      return 'badge-orange background-orange pt-07';
    case 'terminated':
      return 'badge-danger background-red pt-07';
    case 'running':
      return 'badge-success pt-07';
  }
  return 'bg-secondary pt-07'; // terminated, completed, archived
}

export function metersToPixels(meters, latitude, zoom) {
  return (
    meters /
    (78271.484 / Math.pow(2, zoom)) /
    Math.cos((latitude * Math.PI) / 180)
  );
}

export function formatQueryParams(query) {
  const keys = Object.keys(query || {});

  if (keys.length > 0) {
    return (
      '?' +
      keys
        .map(
          (k) =>
            k +
            '=' +
            (typeof query[k] === 'undefined' || query[k] === null
              ? ''
              : query[k])
        )
        .join('&')
    );
  }

  return '';
}

export function getDesignStudioParams(config, extra = {}) {
  const query: any = {};
  if (config != null && typeof config == 'object') {
    if (config.cart) {
      query.cart = config.cart || localStorage.getItem('cartId');
    }
  }
  return formatQueryParams({ ...query, ...extra });
}

/**
 *
 * @param {{[key]: any}} needle
 * @param {Array} haystack
 * @param {string} key
 * @returns {Array<string>} Path to node
 */
export function depthSearch(
  needle: any,
  haystack: Array<Record<string, any>>,
  key = 'id',
  childrenKey = 'children'
) {
  if (!haystack || !needle) return null;

  const stack = [...haystack.slice().reverse()];
  const currentPath: Array<any> = [];

  while (stack.length > 0) {
    const current = stack.pop()!;
    currentPath.push(current[key]);
    if (current[key] === needle) {
      return currentPath;
    } else if (current[childrenKey] && current[childrenKey].length > 0) {
      stack.push(...current[childrenKey].slice().reverse());
    } else {
      currentPath.pop();
    }
  }

  return null;
}

export function getPrefillName(prefix: string, suffix?: string) {
  return prefix + ' - ' + format(new Date(), 'PPp') + (suffix ? ` - ${suffix}` : '');
}

export function generateWebhookUrl() {
  const random = getRandomAlphaNumeric(10);
  return `${process.env.NEXT_PUBLIC_API_URL}/external/${random}`;
}

export const QR_FIELDS = {
  url: ['url'],
  sms: ['phone', 'message'],
  call: ['phone'],
  email: ['email', 'subject', 'body'],
  location: ['location'],
};


export const stylesMode = {
  light: '[data-mui-color-scheme="light"] &',
  dark: '[data-mui-color-scheme="dark"] &',
};

export const mediaQueries = {
  upXs: '@media (min-width:0px)',
  upSm: '@media (min-width:600px)',
  upMd: '@media (min-width:900px)',
  upLg: '@media (min-width:1200px)',
  upXl: '@media (min-width:1536px)',
};

/**
 * Converts a hex color to RGB channels
 */
export function hexToRgbChannel(hex) {
  if (!/^#[0-9A-F]{6}$/i.test(hex)) {
    throw new Error(`Invalid hex color: ${hex}`);
  }

  const r = parseInt(hex.substring(1, 3), 16);
  const g = parseInt(hex.substring(3, 5), 16);
  const b = parseInt(hex.substring(5, 7), 16);

  return `${r} ${g} ${b}`;
}

/**
 * Converts a hex color to RGB channels
 */
export function createPaletteChannel(hexPalette) {
  const channelPalette = {};

  Object.entries(hexPalette).forEach(([key, value]) => {
    channelPalette[`${key}Channel`] = hexToRgbChannel(value);
  });

  return { ...hexPalette, ...channelPalette };
}

/**
 * Color with alpha channel
 */
export function varAlpha(color, opacity = 1) {
  if (!color) return `rgba(0 0 0 / ${opacity})`;

  const unsupported =
    color.startsWith('#') ||
    color.startsWith('rgb') ||
    color.startsWith('rgba') ||
    (!color.includes('var') && color.includes('Channel'));

  if (unsupported) {
    throw new Error(`[Alpha]: Unsupported color format "${color}".
     Supported formats are:
     - RGB channels: "0 184 217".
     - CSS variables with "Channel" prefix: "var(--palette-common-blackChannel, #000000)".
     Unsupported formats are:
     - Hex: "#00B8D9".
     - RGB: "rgb(0, 184, 217)".
     - RGBA: "rgba(0, 184, 217, 1)".
     `);
  }

  return `rgba(${color} / ${opacity})`;
}


/**
 * Set font family
 */
export function setFont(fontName) {
  return `"${fontName}",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol"`;
}

/**
 * Converts rem to px
 */
export function remToPx(value) {
  return Math.round(parseFloat(value) * 16);
}

/**
 * Converts px to rem
 */
export function pxToRem(value) {
  return `${value / 16}rem`;
}

/**
 * Responsive font sizes
 */
export function responsiveFontSizes({ sm, md, lg }) {
  return {
    [mediaQueries.upSm]: { fontSize: pxToRem(sm) },
    [mediaQueries.upMd]: { fontSize: pxToRem(md) },
    [mediaQueries.upLg]: { fontSize: pxToRem(lg) },
  };
}


export function orderBy(array, properties, orders) {
  return array.slice().sort((a, b) => {
    for (let i = 0; i < properties.length; i += 1) {
      const property = properties[i];
      const order = orders && orders[i] === 'desc' ? -1 : 1;

      const aValue = a[property];
      const bValue = b[property];

      if (aValue < bValue) return -1 * order;
      if (aValue > bValue) return 1 * order;
    }
    return 0;
  });
}

export function isEqual(a, b) {
  if (a === null || a === undefined || b === null || b === undefined) {
    return a === b;
  }

  if (typeof a !== typeof b) {
    return false;
  }

  if (typeof a === 'string' || typeof a === 'number' || typeof a === 'boolean') {
    return a === b;
  }

  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) {
      return false;
    }

    return a.every((item, index) => isEqual(item, b[index]));
  }

  if (typeof a === 'object' && typeof b === 'object') {
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);

    if (keysA.length !== keysB.length) {
      return false;
    }

    return keysA.every((key) => isEqual(a[key], b[key]));
  }

  return false;
}

export function isExternalLink(url) {
  return url.startsWith('http');
}

/**
 * Whether agentic-style UI (personalization, Enhance with AI, etc.) is shown.
 * Opt-out only: `localStorage.setItem("agentic", "false")` disables on the client.
 *
 * SSR must match the client default (`stored !== "false"`) so Next.js does not
 * hydrate mismatched trees (e.g. Stripo toolbar / More menu missing on staging).
 */
export function isAgenticEnabled(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }

  try {
    const stored = localStorage.getItem("agentic");
    return stored !== "false";
  } catch (error) {
    console.error("Error loading agentic state:", error);
    return true;
  }
}

/**
 * Creates a debounced function that delays invoking func until after wait milliseconds
 * have elapsed since the last time the debounced function was invoked.
 * @param func - The function to debounce
 * @param wait - The number of milliseconds to delay
 * @returns The debounced function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout> | null = null

  return function (this: any, ...args: Parameters<T>) {
    const context = this

    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }

    timeoutId = setTimeout(() => {
      func.apply(context, args)
    }, wait)
  }
}


export const getStatusColor = (status: string) => {
  switch (status) {
    case "draft":
      return "default"; // Grey
    case "pending":
      return "warning"; // Yellow/Orange
    case "ready":
    case "active":
      return "success"; // Green
    case "paused":
      return "info"; // Blue
    case "terminated":
      return "error"; // Red
    case "completed":
      return "success"; // Green
    default:
      return "default"; // Default Grey for unknown statuses
  }
};